import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  useToast,
  RadioGroup,
  Radio,
  Stack,
  Progress,
  Icon,
  Heading,
} from '@chakra-ui/react';
import { FiCheck, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { FaFlask, FaDna, FaUserGraduate, FaBuilding } from 'react-icons/fa';
import { BACKEND_API } from '../../constants/backendApiEndpoints';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface OptionCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
}

const OptionCard: React.FC<OptionCardProps> = ({ icon, title, description, isSelected, onClick }) => (
  <Box
    as="button"
    onClick={onClick}
    p={4}
    borderRadius="lg"
    border="2px solid"
    borderColor={isSelected ? 'purple.500' : 'gray.200'}
    bg={isSelected ? 'purple.50' : 'white'}
    w="100%"
    textAlign="left"
    transition="all 0.2s"
    _hover={{ borderColor: isSelected ? 'purple.500' : 'purple.200', bg: isSelected ? 'purple.50' : 'gray.50' }}
  >
    <HStack spacing={3}>
      <Icon as={icon} boxSize={6} color={isSelected ? 'purple.500' : 'gray.500'} />
      <Box>
        <Text fontWeight="600" color={isSelected ? 'purple.700' : 'gray.700'}>{title}</Text>
        <Text fontSize="sm" color="gray.500">{description}</Text>
      </Box>
      {isSelected && <Icon as={FiCheck} color="purple.500" ml="auto" />}
    </HStack>
  </Box>
);

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onComplete, onSkip }) => {
  const [step, setStep] = useState(1);
  const [organizationType, setOrganizationType] = useState<string>('');
  const [samplesPerMonth, setSamplesPerMonth] = useState<string>('');
  const [sequencingApproach, setSequencingApproach] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const totalSteps = 3;

  const handleNext = () => {
    if (step === 1 && !organizationType) {
      toast({ title: 'Please select an option', status: 'warning', duration: 2000 });
      return;
    }
    if (step === 2 && !samplesPerMonth) {
      toast({ title: 'Please select an option', status: 'warning', duration: 2000 });
      return;
    }
    if (step === 3 && !sequencingApproach) {
      toast({ title: 'Please select an option', status: 'warning', duration: 2000 });
      return;
    }
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(BACKEND_API.USER.ONBOARDING_COMPLETE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_type: organizationType,
          samples_per_month: samplesPerMonth,
          sequencing_approach: sequencingApproach,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to complete onboarding');
      }

      toast({
        title: 'Welcome to Seq4Life!',
        description: 'Your profile has been set up successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onComplete();
    } catch (error) {
      console.error('Onboarding error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to complete onboarding',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <VStack spacing={4} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="md" color="gray.700">What type of organization are you?</Heading>
              <Text color="gray.500" mt={1}>This helps us tailor your experience</Text>
            </Box>
            <OptionCard
              icon={FaBuilding}
              title="Service Provider"
              description="Offering genomic analysis services to clients"
              isSelected={organizationType === 'service_provider'}
              onClick={() => setOrganizationType('service_provider')}
            />
            <OptionCard
              icon={FaDna}
              title="Genetic Lab"
              description="Research or clinical genetics laboratory"
              isSelected={organizationType === 'genetic_lab'}
              onClick={() => setOrganizationType('genetic_lab')}
            />
            <OptionCard
              icon={FaUserGraduate}
              title="Researcher"
              description="Academic or independent researcher"
              isSelected={organizationType === 'researcher'}
              onClick={() => setOrganizationType('researcher')}
            />
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={4} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="md" color="gray.700">How many samples do you process monthly?</Heading>
              <Text color="gray.500" mt={1}>On average, how many samples do you analyze?</Text>
            </Box>
            <OptionCard
              icon={FaFlask}
              title="0 - 10 samples"
              description="Small-scale or occasional processing"
              isSelected={samplesPerMonth === '0-10'}
              onClick={() => setSamplesPerMonth('0-10')}
            />
            <OptionCard
              icon={FaFlask}
              title="10 - 50 samples"
              description="Medium-scale regular processing"
              isSelected={samplesPerMonth === '10-50'}
              onClick={() => setSamplesPerMonth('10-50')}
            />
            <OptionCard
              icon={FaFlask}
              title="100+ samples"
              description="High-throughput processing"
              isSelected={samplesPerMonth === '100+'}
              onClick={() => setSamplesPerMonth('100+')}
            />
          </VStack>
        );

      case 3:
        return (
          <VStack spacing={4} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="md" color="gray.700">How do you handle DNA sequencing?</Heading>
              <Text color="gray.500" mt={1}>Do you perform sequencing in-house or outsource it?</Text>
            </Box>
            <OptionCard
              icon={FaDna}
              title="In-house"
              description="We have our own sequencing equipment"
              isSelected={sequencingApproach === 'in_house'}
              onClick={() => setSequencingApproach('in_house')}
            />
            <OptionCard
              icon={FaDna}
              title="Outsourced"
              description="We send samples to external sequencing providers"
              isSelected={sequencingApproach === 'outsourced'}
              onClick={() => setSequencingApproach('outsourced')}
            />
            <OptionCard
              icon={FaDna}
              title="Both"
              description="We use a combination of in-house and outsourced"
              isSelected={sequencingApproach === 'both'}
              onClick={() => setSequencingApproach('both')}
            />
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onSkip} 
      closeOnOverlayClick={true} 
      closeOnEsc={true}
      isCentered
      size="lg"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent mx={4} borderRadius="xl" overflow="hidden">
        <ModalHeader bg="purple.600" color="white" py={6}>
          <VStack spacing={1}>
            <Text fontSize="xl" fontWeight="bold">Welcome to Seq4Life!</Text>
            <Text fontSize="sm" fontWeight="normal" opacity={0.9}>
              Let's set up your profile
            </Text>
          </VStack>
        </ModalHeader>

        <Box px={6} pt={4}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.500">Step {step} of {totalSteps}</Text>
            <Text fontSize="sm" color="gray.500">{Math.round((step / totalSteps) * 100)}%</Text>
          </HStack>
          <Progress 
            value={(step / totalSteps) * 100} 
            size="sm" 
            colorScheme="purple" 
            borderRadius="full"
          />
        </Box>

        <ModalBody py={6}>
          {renderStep()}
        </ModalBody>

        <ModalFooter borderTop="1px" borderColor="gray.100" gap={3}>
          <Button 
            variant="ghost" 
            onClick={onSkip}
            color="gray.500"
          >
            Skip for now
          </Button>
          {step > 1 && (
            <Button 
              variant="ghost" 
              onClick={handleBack}
              leftIcon={<FiChevronLeft />}
            >
              Back
            </Button>
          )}
          <Button
            colorScheme="purple"
            onClick={handleNext}
            isLoading={isSubmitting}
            loadingText="Completing..."
            rightIcon={step === totalSteps ? <FiCheck /> : <FiChevronRight />}
            ml="auto"
          >
            {step === totalSteps ? 'Complete' : 'Next'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OnboardingModal;
